import * as PIXI from "pixi.js-legacy"

import * as constants from "./constants"

export default class TransitionManager {

	get isRunning() { return (this._transitionData) ? this._transitionData.isRunning : false }

	constructor(player, contentContainer) {
		this._player = player
		this._contentContainer = contentContainer

		// Create the two containers used for handling transitions
		this._oldPageContainer = new PIXI.Container()
		this._newPageContainer = new PIXI.Container()

		// The _sequenceSlicesArray will hold existing sequenceSlices
		this._sequenceSlicesArray = []
		// The videoSlicesArray will hold existing videoSlices
		this._videoSlicesArray = []

		this._reset()
	}

	_reset() {
		this._transitionData = { isRunning: false, shouldForceToEnd: false }
	}

	// Add a sequence slice to the list
	addSequenceSlice(sequenceSlice) {
		this._sequenceSlicesArray.push(sequenceSlice)
	}

	// Add a video slice to the list
	addVideoSlice(videoSlice) {
		this._videoSlicesArray.push(videoSlice)
	}

	// Once the PIXI loader has loaded all textures, add them to the sequence and video slices
	finishBuildingSlices() {
		this._sequenceSlicesArray.forEach((sequenceSlice) => {
			sequenceSlice.finishBuilding()
		})
		this._videoSlicesArray.forEach((videoSlice) => {
			videoSlice.finishBuilding()
		})
	}

	// Run the transition between oldPage and newPage
	run(transition, newPage, oldPage, isGoingForward) {
		this._transitionData = {
			...this.transitionData,
			...transition,
			isRunning: true,
			newPage,
			oldPage,
			isGoingForward,
		}

		// Populate newPageContainer with resources from the new page (hiding the container)
		this._newPageContainer.visible = false
		newPage.pushContentIntoContainer(this._newPageContainer)

		// In the case of a sequence transition, use the oldPage container to play the sequence
		// (_newPageContainer is already positioned below just in case there is no sequence)
		if (transition.type === "sequence") {
			this._contentContainer.removeChildren()
			this._contentContainer.addChild(this._newPageContainer)
			this._contentContainer.addChild(this._oldPageContainer)

			if (!transition.sequenceSlice || transition.sequenceSlice.duration === 0) {
				this._transitionData.duration = 0
			} else {
				this._transitionData.duration = transition.duration || constants.defaultDuration

				const { sequenceSlice } = transition
				sequenceSlice.resize()
				sequenceSlice.addSliceContainerToPageContainer(this._oldPageContainer)

				// Run the _sequenceSlice's animation
				sequenceSlice.play()
			}

		// Similarly, the oldPage container plays the video in the case of a video transition
		// (_newPageContainer is already positioned below just in case there is no video)
		} else if (transition.type === "video") {
			this._contentContainer.removeChildren()
			this._contentContainer.addChild(this._newPageContainer)
			this._contentContainer.addChild(this._oldPageContainer)

			if (!transition.videoSlice || transition.videoSlice.duration === 0) {
				this._transitionData.duration = 0
			} else {
				// If no duration was specified for the transition,
				// the video's duration will be used
				this._transitionData.duration = transition.duration
					|| (transition.videoSlice.duration * 1000)

				const { videoSlice } = transition
				videoSlice.resize()
				videoSlice.addSliceContainerToPageContainer(this._oldPageContainer)

				// Play the _videoSlice
				videoSlice.play()
			}

		// If the transition is neither a sequence nor a video transition
		} else {
			// Put all the current page's resources in oldPageContainer (which empties contentContainer)
			this._oldPageContainer.addChild(...this._contentContainer.children)

			this._transitionData.duration = transition.duration || constants.defaultDuration

			// In the case of a slide-out, the _newPageContainer is below the _oldPageContainer
			if (transition.type === "slide-out") {
				this._contentContainer.addChild(this._newPageContainer)
				this._contentContainer.addChild(this._oldPageContainer)
			// Otherwise the _newPageContainer is placed above the _oldPageContainer
			} else {
				this._contentContainer.addChild(this._oldPageContainer)
				this._contentContainer.addChild(this._newPageContainer)
			}
		}

		// Now start running the loop handling the changes during the transition
		this._transitionData.startTime = Date.now()
		this._transitionLoop()
	}

	_transitionLoop() {
		const {
			shouldForceToEnd,
			type,
			direction,
			duration,
			startTime,
		} = this._transitionData

		const elapsedTime = Date.now() - startTime

		// If the transition has ended or the user has forced its ending, end it
		if (elapsedTime >= duration || shouldForceToEnd === true) {
			this._endTransition()

		// Otherwise just apply the required changes based on time
		} else {
			const percent = elapsedTime / duration
			const { viewportSize } = this._player
			const { width, height } = viewportSize
			if (type === "dissolve") {
				this._newPageContainer.alpha = percent
			}
			if (type === "slide-in" || type === "push") {
				switch (direction) {
				case "ltr":
					this._newPageContainer.position.x = (percent - 1) * width
					break
				case "rtl":
					this._newPageContainer.position.x = (1 - percent) * width
					break
				case "ttb":
					this._newPageContainer.position.y = (percent - 1) * height
					break
				case "btt":
					this._newPageContainer.position.y = (1 - percent) * height
					break
				default:
					break
				}
			}
			if (type === "slide-out" || type === "push") {
				switch (direction) {
				case "ltr":
					this._oldPageContainer.position.x = percent * width
					break
				case "rtl":
					this._oldPageContainer.position.x = -percent * width
					break
				case "ttb":
					this._oldPageContainer.position.y = percent * height
					break
				case "btt":
					this._oldPageContainer.position.y = -percent * height
					break
				default:
					break
				}
			}
			// Do not forget to make the _newPageContainer visible now
			this._newPageContainer.visible = true
			requestAnimationFrame(this._transitionLoop.bind(this))
		}
	}

	_endTransition() {
		const {
			sequenceSlice,
			videoSlice,
			newPage,
			oldPage,
			endCallback,
		} = this._transitionData

		// Reset positions and visibility
		this._oldPageContainer.position = { x: 0, y: 0 }
		this._newPageContainer.position = { x: 0, y: 0 }
		this._newPageContainer.alpha = 1
		this._newPageContainer.visible = true

		// Feed the _contentContainer directly with the new page's data
		// (which empties the _newPageContainer)
		this._contentContainer.addChild(...this._newPageContainer.children)

		// Stop the sequenceSlice's _playableSprite and set it back to zero if there is one
		if (sequenceSlice) {
			sequenceSlice.gotoAndStop(0)
		}
		// Stop the videoSlice's _playableSprite and set it back to zero if there is one
		if (videoSlice) {
			videoSlice.stop()
		}

		// Now remove all traces of transition containers and their children
		this._contentContainer.removeChild(this._newPageContainer)
		this._oldPageContainer.removeChildren()
		this._contentContainer.removeChild(this._oldPageContainer)

		this._reset()

		// Run a callback if required
		if (endCallback) {
			endCallback()
		}

		// If page videos need to be played or stopped
		if (newPage) {
			newPage.play()
		}
		if (oldPage) {
			oldPage.stop()
		}
	}

	forceTransitionToEnd(callback, isGoingForward) {
		if (!this._transitionData) {
			return
		}
		// Only run the callback if the movement directions differ
		if (isGoingForward !== this._transitionData.isGoingForward) {
			this._transitionData.endCallback = callback
		}
		this._transitionData.shouldForceToEnd = true
	}

	resize() {
		if (this._transitionData && this._transitionData.isRunning === true) {
			const {
				sequenceSlice,
				videoSlice,
				newPage,
				oldPage,
			} = this._transitionData
			if (sequenceSlice) {
				sequenceSlice.resize()
			}
			if (videoSlice) {
				videoSlice.resize()
			}
			if (oldPage) {
				oldPage.resize()
			}
			if (newPage) {
				newPage.resize()
			}
		}
	}

	destroy() {
		this._sequenceSlicesArray.forEach((slice) => {
			slice.destroy()
		})
		this._videoSlicesArray.forEach((slice) => {
			slice.destroy()
		})
	}

}